import { createFileRoute } from "@tanstack/react-router"
import { CircleCheck, CircleX } from "lucide-react"

import { TRIAL_DAYS } from "@/brand/plan-numbers"
import config from "@/config"
import { localizeHref } from "@/core/i18n/locale-context"
import { breadcrumbJsonLd, buildHead } from "@/core/seo"

import {
  capKm,
  displayYear,
  EXAMPLE_TRIP,
  HOME_FINANCIAL_YEAR,
  rate,
} from "../../components/home/home-content"
import { AnswerBox, Breadcrumb, CtaBand, FaqAccordion, SourceLine } from "../../components/landing"
import { GlanceCard, HeroBand } from "../../components/landing/page-parts"
import { RIDESHARE_CHECKED, SRC } from "../../content/pages/rideshare-sources"
import { formatDateZh } from "../../content/zh/dates"

/**
 * Chinese version of /rideshare-drivers (D-WEB-LANG-SCOPE). Every statement is the English one, which
 * was checked against the ATO page named beside it; the links come from the same constants, so they
 * cannot drift. Figures ($82.50, $75,000, 28 days, cents per km) are the English page's.
 */
const checked = formatDateZh(RIDESHARE_CHECKED)

const ZH_LABELS: Record<keyof typeof SRC, string> = {
  registrations: "ATO，ride-sourcing 登记",
  gst: "ATO，ride-sourcing：申报和缴纳 GST",
  income: "ATO，ride-sourcing 的收入和扣除额",
  records: "ATO，ride-sourcing 记录保存",
  invoices: "ATO，ride-sourcing 的 Tax Invoice",
  services: "ATO，通过平台提供服务",
  activities: "ATO，共享经济的活动类型",
  howGst: "ATO，GST 如何运作",
  methods: "ATO，车辆开支计算方法",
}
const src = (key: keyof typeof SRC) => ({ label: ZH_LABELS[key], href: SRC[key].href })

export const rideshareZhSeo = () =>
  buildHead({
    title: "网约车与外卖司机：ABN、GST、BAS",
    description:
      "ATO 对网约车和外卖司机的要求：ABN、GST 登记、BAS、车辆开支和记录，每一条都附 ATO 来源和核对日期。",
    path: "/rideshare-drivers",
    localePrefix: "zh",
    jsonLd: [breadcrumbJsonLd([{ name: "首页", path: "/zh" }, { name: "网约车与外卖司机" }])],
  })

export const Route = createFileRoute("/_brand-layout/zh/rideshare-drivers")({
  head: () => {
    const { meta, links, scripts } = rideshareZhSeo()
    return { meta, links, scripts }
  },
  component: RideshareZhPage,
})

const linkClass =
  "focus-visible:ring-ring rounded-sm underline underline-offset-2 outline-none focus-visible:ring-2"
const L = (path: string) => localizeHref(path, "zh")

const FAQ = [
  {
    question: "平台会替我开 Tax Invoice 吗？",
    answer: (
      <>
        可能会。你登记 GST 之后，把 ABN 提供给平台，平台可能会替你开 Tax Invoice。
        <SourceLine sources={[src("registrations")]} checked={checked} />
      </>
    ),
  },
  {
    question: "什么时候必须给乘客开 Tax Invoice？",
    answer: (
      <>
        如果乘客就一笔含 GST 超过 A$82.50 的车费要求开 Tax Invoice，你必须在 28 天内提供。不超过
        A$82.50 的车费，只需要开普通发票。
        <SourceLine sources={[src("invoices")]} checked={checked} />
      </>
    ),
  },
  {
    question: "我需要报税吗？",
    answer: (
      <>
        ATO 说，因为你在经营业务，无论 ride-sourcing
        赚多少，你都要提交报税表。平台给的小费和奖金也算收入。
        <SourceLine sources={[src("income")]} checked={checked} />
      </>
    ),
  },
]

export function RideshareZhPage() {
  const year = displayYear(HOME_FINANCIAL_YEAR)
  const rateChecked = formatDateZh(rate.checkedOn)
  const trip = EXAMPLE_TRIP.route.replace(" to ", " 到 ")
  return (
    <>
      <Breadcrumb items={[{ label: "首页", href: "/" }, { label: "网约车与外卖司机" }]} />
      <HeroBand className="md:grid-cols-[1.4fr_1fr] md:gap-6">
        <div className="grid content-start gap-4">
          <p className="text-muted-foreground text-sm">指南 · 最近复核 {checked}</p>
          <h1 className="text-3xl font-bold tracking-tight md:text-5xl">
            网约车与外卖司机：ABN、GST、BAS 和车辆开支
          </h1>
          <AnswerBox source={<SourceLine sources={[src("registrations")]} checked={checked} />}>
            如果你开车跑网约车（ATO 称 ride-sourcing）平台，无论收入多少，都需要有
            ABN，并且要在第一单之前登记 GST（商品及服务税）。外卖工作则要看你是怎样受雇或合作的，ATO
            的指引不同，请查阅它的共享经济页面，对照你的情况。之后你在 BAS 上申报
            GST，并可以申报车辆开支。
          </AnswerBox>
        </div>
        <div
          className="border-border bg-background grid content-start gap-2 rounded-2xl border p-5 shadow-sm"
          role="img"
          aria-label={`行程卡片示例：${trip}，${EXAMPLE_TRIP.km} km，每公里 ${rate.cents} 分，${EXAMPLE_TRIP.amount}。`}
        >
          <span aria-hidden="true" className="text-muted-foreground text-sm">
            示例行程，由 GPS 记录
          </span>
          <b aria-hidden="true" className="text-lg">
            {trip}
          </b>
          <span aria-hidden="true" className="text-muted-foreground">
            {EXAMPLE_TRIP.km} km × {rate.cents} 分/km
          </span>
          <span aria-hidden="true" className="text-xl font-bold">
            {EXAMPLE_TRIP.amount}
          </span>
        </div>
      </HeroBand>

      <section className="px-band grid gap-4 py-8" aria-labelledby="glance-title">
        <h2 id="glance-title" className="text-2xl font-semibold">
          一眼看清
        </h2>
        <div className="grid gap-4 md:grid-cols-2">
          <GlanceCard
            icon={CircleCheck}
            tone="success"
            mark="check"
            title="你可以"
            items={[
              "用 cents per km 法或 logbook 法申报车辆的业务用途",
              `${year} 财年每辆车最多申报 ${capKm} 个业务公里，每公里 ${rate.cents} 分`,
            ]}
          />
          <GlanceCard
            icon={CircleX}
            tone="warning"
            mark="cross"
            title="你不可以"
            items={[
              "开网约车却等到营业额达到 A$75,000 的 GST 门槛才登记：要在第一单之前登记",
              "给不符合 ATO 对 car 定义的车辆用 cents per km 法或 logbook 法：它要按实际开支计算",
            ]}
          />
        </div>
      </section>

      <section className="px-band grid gap-3 py-8" aria-labelledby="abn-title">
        <h2 id="abn-title" className="text-2xl font-semibold">
          我需要 ABN 吗？
        </h2>
        <p className="max-w-[70ch]">
          网约车司机需要：登记 GST 必须先有 ABN。外卖工作请查看 ATO 的
          <a href={SRC.services.href} className={linkClass}>
            通过平台提供服务页面
          </a>
          （英文），对照你的情况。
        </p>
        <SourceLine sources={[src("registrations")]} checked={checked} />
      </section>

      <section className="px-band grid gap-3 py-8" aria-labelledby="gst-title">
        <h2 id="gst-title" className="text-2xl font-semibold">
          GST
        </h2>
        <p className="max-w-[70ch]">
          在 GST 方面，网约车按出租车运输处理。常规的 A$75,000
          营业额门槛不适用，所以你要在第一单之前登记 GST。GST
          按全额车费计算，你可以选择按月或按季申报，不能选择按年申报。
        </p>
        <SourceLine sources={[src("registrations"), src("gst")]} checked={checked} />
      </section>

      <section className="px-band grid gap-3 py-8" aria-labelledby="bas-title">
        <h2 id="bas-title" className="text-2xl font-semibold">
          BAS
        </h2>
        <p className="max-w-[70ch]">
          登记之后，你在 BAS（业务活动报表）上申报并缴纳收取的 GST，也可以就业务采购申请 GST 抵扣。
          {config.core.projectName} 显示你的行程和收据，不代你提交 BAS。
        </p>
        <SourceLine sources={[src("gst")]} checked={checked} />
      </section>

      <section className="px-band grid gap-3 py-8" aria-labelledby="car-title">
        <h2 id="car-title" className="text-2xl font-semibold">
          车辆开支
        </h2>
        <p className="max-w-[70ch]">
          对汽车（car），你可以用 cents per km 法（每辆车最多 {capKm} 个业务公里，{year} 财年每公里{" "}
          {rate.cents} 分）或 logbook 法。不符合 ATO 对 car 定义的车辆，按实际开支法计算。
        </p>
        <p className="max-w-[70ch]">
          只有业务公里才算，而且要看行程的目的。按 ATO
          的例子，上下班途中开着应用，只有从接单到送达的公里才算；为了找乘客而四处开车，则全部算。
        </p>
        <SourceLine sources={[src("methods"), src("income")]} checked={checked} />
        <SourceLine
          sources={[{ label: "ATO，cents per km 费率", href: rate.sourceUrl }]}
          financialYear={year}
          checked={rateChecked}
        />
      </section>

      <section className="px-band grid gap-3 py-8" aria-labelledby="records-title">
        <h2 id="records-title" className="text-2xl font-semibold">
          记录
        </h2>
        <p className="max-w-[70ch]">
          ATO
          列出的记录包括：显示你收入的平台报表、你申报开支的收据，以及行车日志和里程表读数。它要求你在提交报税表之后再保存记录
          5 年。
        </p>
        <SourceLine sources={[src("records"), src("income")]} checked={checked} />
      </section>

      <section className="px-band grid gap-3 py-8" aria-labelledby="delivery-title">
        <h2 id="delivery-title" className="text-2xl font-semibold">
          外卖司机
        </h2>
        <p className="max-w-[70ch]">
          ATO
          把外卖和快递服务（例如送餐）列为共享经济活动。你赚到的钱是应税收入，要填进报税表。是否需要登记
          GST，要看你是怎样受雇或合作的以及你的营业额，所以请先查看 ATO
          的页面，或在决定前向注册税务代理咨询。
        </p>
        <SourceLine
          sources={[src("activities"), src("services"), src("howGst")]}
          checked={checked}
        />
      </section>

      <section className="px-band grid gap-4 py-8" aria-labelledby="faq-title">
        <h2 id="faq-title" className="text-2xl font-semibold">
          常见问题
        </h2>
        <FaqAccordion items={FAQ} />
        <p className="text-muted-foreground max-w-[70ch] text-sm">
          仅为估算和一般性信息，不是税务建议。更多问题见
          <a href={L("/faq")} className={linkClass}>
            常见问题
          </a>
          。
        </p>
      </section>

      <CtaBand
        title="把你的行车记录理清楚"
        description={`免费试用 ${TRIAL_DAYS} 天，无需绑定银行卡。`}
        ctaLabel="开始免费试用"
        ctaHref="/login"
      />
    </>
  )
}
